import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Send, Search, Mail } from 'lucide-react';
import { useAdminLang } from '../../hooks/useAdminLang';

// Admin — send an email to any user (green quick action)
export const AdminEmailModal = ({ onClose }) => {
  const { at } = useAdminLang();
  const [query, setQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    axios.get('/api/admin/sent-emails?limit=5').then(r => setHistory(r.data.items || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (query.trim().length < 2) { setUsers([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await axios.get('/api/admin/users?limit=500');
        const all = r.data.users || r.data.items || r.data || [];
        const q = query.toLowerCase();
        setUsers(all.filter(u => (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q) || (u.username || '').toLowerCase().includes(q)).slice(0, 8));
      } catch { setUsers([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const send = async () => {
    if (!selected || !subject.trim() || !body.trim()) {
      toast.error(at('emailMissingFields') || 'Destinataire, titre et texte requis');
      return;
    }
    setSending(true);
    try {
      const r = await axios.post('/api/admin/send-user-email', { user_id: selected.id, subject, body });
      toast.success(`${at('emailSentOk') || 'Email envoyé à'} ${r.data.sent_to}`);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[9999] flex items-center justify-center p-4" onClick={onClose} data-testid="admin-email-modal">
      <div className="bg-gray-900 border border-green-500/30 rounded-3xl max-w-md w-full p-5 max-h-[88vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-green-400" />
            <h3 className="font-bold text-white">{at('emailModalTitle') || 'Envoyer un email'}</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full" data-testid="admin-email-close"><X className="w-5 h-5 text-white" /></button>
        </div>

        {/* Recipient */}
        {selected ? (
          <div className="flex items-center justify-between p-3 bg-green-500/10 border border-green-500/30 rounded-xl mb-3" data-testid="admin-email-selected-user">
            <div>
              <p className="text-sm font-semibold text-white">{selected.name}</p>
              <p className="text-xs text-gray-400">{selected.email}</p>
            </div>
            <button onClick={() => setSelected(null)} className="text-xs text-gray-400 hover:text-white">✕</button>
          </div>
        ) : (
          <div className="mb-3">
            <div className="relative">
              <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder={at('emailPickUser') || 'Rechercher un utilisateur…'}
                className="w-full bg-white/5 border border-white/10 rounded-xl pl-9 pr-3 py-2.5 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-green-500/60"
                data-testid="admin-email-user-search"
              />
            </div>
            {users.length > 0 && (
              <div className="mt-2 bg-black/40 border border-white/10 rounded-xl divide-y divide-white/5 max-h-44 overflow-y-auto">
                {users.map(u => (
                  <button key={u.id} onClick={() => { setSelected(u); setUsers([]); setQuery(''); }} className="w-full text-left px-3 py-2 hover:bg-white/5" data-testid={`admin-email-user-${u.id}`}>
                    <p className="text-sm text-white">{u.name}</p>
                    <p className="text-xs text-gray-500">{u.email}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <input
          value={subject}
          onChange={e => setSubject(e.target.value)}
          placeholder={at('emailSubjectPh') || 'Titre de l\'email'}
          maxLength={200}
          className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-gray-500 mb-3 focus:outline-none focus:border-green-500/60"
          data-testid="admin-email-subject"
        />
        <textarea
          value={body}
          onChange={e => setBody(e.target.value)}
          placeholder={at('emailBodyPh') || 'Texte du message…'}
          rows={6}
          className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder-gray-500 mb-4 resize-none focus:outline-none focus:border-green-500/60"
          data-testid="admin-email-body"
        />
        <button
          onClick={send}
          disabled={sending}
          className="w-full py-3 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full font-bold text-white disabled:opacity-50 flex items-center justify-center gap-2"
          data-testid="admin-email-send"
        >
          <Send className="w-4 h-4" /> {sending ? '…' : (at('emailSendBtn') || 'Envoyer')}
        </button>

        {history.length > 0 && (
          <div className="mt-4">
            <p className="text-[11px] font-semibold text-gray-400 mb-1.5">{at('emailHistory') || 'Derniers envois'}</p>
            {history.map(h => (
              <p key={h.id} className="text-[11px] text-gray-500 truncate">→ {h.user_email} · {h.subject}</p>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminEmailModal;
